import type { ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';

/**
 * A message about the state of the thing on screen — R-050's InlineAlert.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * Sixty-one hand-rolled boxes across thirty-four files, agreeing on nothing.
 * Some carried `role="alert"` and some did not; some announced a failure with a
 * border and some without; one reached for a colour token that does not exist,
 * which would have rendered an error message in ordinary body text on a white
 * card. That last one is the reason this is a component: a failure the reader
 * cannot see is worse than no message at all.
 *
 * ── The tones ───────────────────────────────────────────────────────────────
 * `danger` is a failure the reader has to act on — a refused command, a
 * validation the server rejected. It is the only tone that carries
 * `role="alert"`, because that is the only one worth interrupting a screen
 * reader for.
 *
 * `exception` is R-050's Orange Exception: a human must intervene — an overdue
 * approval, a late order. Its text is the dark exception ink, never white on
 * Orange.
 *
 * `warning` is a fact worth noticing that blocks nothing: a stale
 * acknowledgement, a supplier that cannot currently be ordered from.
 *
 * `success` confirms something happened. `info` is context, on R-050's info
 * surface.
 *
 * ── The action ──────────────────────────────────────────────────────────────
 * InlineAlert's trailing control — the one thing the reader can do about what
 * the alert says ("Review queue"). Optional, because most alerts are a
 * statement, and an action with no real outcome is worse than none.
 */
export type AlertTone = 'danger' | 'warning' | 'success' | 'info' | 'exception';

const TONES: Record<AlertTone, string> = {
  danger: 'border-critical-line bg-critical-surface text-critical-ink',
  warning: 'border-warning-line bg-warning-surface text-warning-ink',
  success: 'border-success-line bg-success-surface text-success-ink',
  // R-050 `.fid-alert--info`: the semantic info surface, border and ink.
  info: 'border-info-line bg-info-surface text-info-ink',
  exception: 'border-exception-line bg-exception-surface text-exception-ink',
};

export function Alert({
  tone = 'info',
  title,
  children,
  action,
  className,
  tourId,
  tourState,
}: {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Guided-tour anchor, rendered as `data-tour` on the root (an `AnchorId`; CONTRACT §3). */
  tourId?: string;
  /**
   * A coach's state probe, rendered as `data-guidance-state` on the root — only
   * with a `tourId`, and only a value `ANCHOR_STATES` lists for it, from SERVER
   * data (UI-6). Never a record ID, a name or an amount.
   */
  tourState?: string;
}) {
  return (
    <div
      data-tour={tourId}
      data-guidance-state={tourId === undefined ? undefined : tourState}
      // Only a failure interrupts. A warning or a confirmation is read in turn.
      role={tone === 'danger' ? 'alert' : undefined}
      className={cn(
        // B-22: the R-050 UI role, so Arabic text gets its own line height.
        'type-ui rounded-(--radius-card) border px-4 py-3',
        action && 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        TONES[tone],
        className,
      )}
    >
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title && 'mt-0.5')}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
